<template>
  <form-control :id="id" :disabled="$attrs.disabled">
    <template slot="control">
      <div
        class="inputDropdown"
        ref="trigger"
        aria-haspopup="listbox"
        tabindex="0"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.prevent="openBox"
        @keydown.down.prevent="nextOption"
        @keydown.up.prevent="prevOption"
        @keydown.esc.prevent="closeBox"
      >
        <input-text
          :id="id"
          v-model="currentValue"
          @click.native="openBox"
          @focus="inputFocused = true"
          @blur="inputFocused = false"
          :disabled="$attrs.disabled"
          :placeholder="placeholder"
          autocomplete="off"
          ref="inputText"
        >
          <!-- @slot (optional) Input label will be passed to Form control container -->
          <slot name="label" slot="label" />
          <!-- @slot (optional) It's a notice, that will appear under the label in small font. Will be passed to Form control container -->
          <slot name="description" slot="description" />
          <!-- @slot (optional) Will be passed to Error message component. Appears in front of error message text -->
          <slot name="error-icon" slot="error-icon" />
          <!-- @slot (optional) Symbol that will appear at the beginning of the input, i.e. search icon -->
          <slot name="prefix" slot="prefix" />
          <!-- @slot (optional) Symbol that will appear at the end of the input, i.e. % -->
          <slot name="suffix" slot="suffix" />
        </input-text>

        <div
          :class="['scroll', { focused }]"
          v-show="opened"
          :open="opened"
          :aria-expanded="opened"
          ref="scrollbar"
          tabindex="0"
        >
          <ul class="options" ref="options" @keydown.enter.prevent.stop="changeValue" tabindex="-1">
            <div class="spinner-wrapper" v-if="loading">
              <!-- @slot (optional) Shows this slot when loading prop is true -->
              <slot name="spinner">
                <spinner />
              </slot>
            </div>
            <template v-else-if="!filteredOptions.length">
              <slot name="bank-selection-error-message" />
            </template>
            <template v-else>
              <li
                v-for="(option, index) in filteredOptions"
                :key="option.key"
                :selected="currentValue === option.value"
                :aria-selected="currentValue === option.value"
                class="option"
                ref="option"
                role="option"
                @click="setResult(option)"
                :class="{ focused: selected === index }"
              >
                <div>{{ option.value }}</div>
                <div class="logo">
                  <img :src="getLogo(option.key)" class="logosize" />
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>
  </form-control>
</template>
<script>
import FormControl from '@/components/FormControl';
import InputText from '@/components/InputText';
import Spinner from '@/components/Spinner';

export const LOGO_URL_DOMAIN = 'https://www.bankstatements.com.au/images/institutions';
/**
 * Like `InputSelect`, but with filtering.
 */
export default {
  name: 'input-dropdown-logo',
  components: {
    FormControl,
    InputText,
    Spinner,
  },
  props: {
    /** id of input element */
    id: {
      type: String,
      required: true,
    },
    /**
     * Array of objects { key, value } where key is dropdown option value and value is label.
     * Any symbols except letters, digits and underscore will be omitted.
     * E.g. key 'My_super long #1' will become mysuperlong1.
     * @values [ { key: 'Purchase inventory', value: 'Purchase inventory' }, ]
     */
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    /** Default value of dropdown when nothing selected */
    defaultValue: {
      type: String,
      default: '',
    },
    /**
     * Selected option object key
     * @model
     */
    value: {
      type: [String, Number],
      default: '',
    },
    /** Show spinner when data isn't ready */
    loading: {
      type: Boolean,
      default: false,
    },
    /** Text that will be shown inside dropdown when nothing selected */
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      focused: false,
      opened: false,
      selected: -1,
      inputFocused: false,
    };
  },
  computed: {
    currentValue: {
      get() {
        return this.value || this.defaultValue;
      },
      set(value) {
        this.openBox();
        /**
         * Event for select some value
         * @property {string|number} value Current selected value
         */
        this.selected = -1;
        this.$emit('input', value);
      },
    },
    filteredOptions() {
      if (this.currentValue && this.currentValue.length) {
        return this.options.filter(option => {
          const lowerCaseValue = option.value.toLowerCase();
          const lowerCaseCurrentValue = this.currentValue.toLowerCase();

          return lowerCaseValue.startsWith(lowerCaseCurrentValue);
        });
      }
      return this.options;
    },
  },
  methods: {
    changeValue() {
      if (this.selected === -1) {
        return;
      }
      const option = this.filteredOptions[this.selected];

      this.currentValue = option.value;
      this.emitOption(option);
      this.closeBox();
    },
    getLogo(slug) {
      return `${LOGO_URL_DOMAIN}/${slug}.png`;
    },
    setResult(option) {
      this.currentValue = option.value;
      this.emitOption(option);

      this.opened = false;
    },
    emitOption(option) {
      this.$emit('change', option);
    },
    openBox() {
      if (!this.$attrs.disabled && !this.opened) {
        this.opened = true;
        this.selected = this.options.findIndex(option => option.key === this.currentValue);
        this.$nextTick().then(() => {
          this.scrollOptionsToSelected();
        });
      }
    },
    setFocusTo(element) {
      this.$nextTick().then(() => {
        element.focus();
      });
    },
    closeBox() {
      this.setErrorMessage('');
      this.opened = false;
      this.$nextTick().then(() => {
        /**
         * Event for closing dropdown
         * @property {string|number} currentValue Current selected value
         */
        this.$emit('closed', this.currentValue);
      });
    },
    nextOption() {
      if (this.selected < this.filteredOptions.length - 1) {
        this.setOption(this.selected + 1);
      }
    },
    prevOption() {
      if (this.selected > 0) {
        this.setOption(this.selected - 1);
      }
    },
    setOption(selected) {
      this.openBox();
      this.setFocusTo(this.$refs.options);
      this.setSelected(selected);
    },
    setSelected(index) {
      this.selected = index;
      this.scrollOptionsToSelected();
    },
    scrollOptionsToSelected() {
      if (this.selected === -1) {
        return;
      }
      const optionsNode = this.$refs.options;
      const selectedNode = this.$refs.option[this.selected];
      if (selectedNode === undefined) {
        return;
      }

      const optionsNodePaddingTop = optionsNode.firstChild.offsetTop;
      const optionsNodePaddingBottom =
        optionsNode.scrollHeight -
        optionsNode.lastChild.offsetTop -
        optionsNode.lastChild.offsetHeight;

      const selectedNodeYRelativeToOptionsTop = selectedNode.offsetTop - optionsNode.scrollTop;

      const selectedNodeIsAboveScreen = selectedNodeYRelativeToOptionsTop < 0;
      const selectedNodeIsBelowScreen =
        selectedNodeYRelativeToOptionsTop + selectedNode.offsetHeight > optionsNode.offsetHeight;

      if (selectedNodeIsBelowScreen) {
        optionsNode.scrollTop =
          selectedNode.offsetTop +
          selectedNode.offsetHeight -
          optionsNode.offsetHeight +
          optionsNodePaddingBottom;
      } else if (selectedNodeIsAboveScreen) {
        optionsNode.scrollTop = selectedNode.offsetTop - optionsNodePaddingTop;
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target) && this.opened) {
        this.selected = -1;
        this.closeBox();
      }
    },
    getValue() {
      return this.currentValue;
    },
    setErrorMessage(msg) {
      const { inputText } = this.$refs;

      inputText.setErrorMessage(msg);
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>
<style lang="sass" scoped>
$input-dropdown-background-color-disabled: #F8F8F8 !default
$input-dropdown-box-shadow-disabled: 0 0 0 1px #F8F8F8 !default

$input-dropdown-scroll-max-height: baseunit(30) !default
$input-dropdown-scroll-border-color: $color-system-neutral-gray-5 !default
$input-dropdown-scroll-border-radius: 4px !default
$input-dropdown-scroll-border: 1px solid $input-dropdown-scroll-border-color !default
$input-dropdown-scroll-margins: $glass-spacing-05 0 !default

$input-dropdown-option-height: baseunit(4.2) !default
$input-dropdown-option-paddings: $glass-spacing-105 $glass-spacing-2 !default
$input-dropdown-option-font-color: $font-color-dark !default
$input-dropdown-option-background-color: $color-system-neutral-white !default
$input-dropdown-option-background-color-active: $color-system-neutral-white !default

$input-dropdown-symbol-width: baseunit(1.1) !default
$input-dropdown-symbol-height: baseunit(1.1) !default
$input-dropdown-symbol-color: $color-system-neutral-gray-5 !default
$input-dropdown-symbol-active-color: $color-system-neutral-gray-5 !default

$input-dropdown-scrollbar-width: baseunit(0.2) !default
$input-dropdown-scrollbar-position-right: $glass-spacing-0125 !default
$input-dropdown-scrollbar-position-vertical: $glass-spacing-05 !default
$input-dropdown-scrollbar-track-width: baseunit(0.2) !default
$input-dropdown-scrollbar-track-height: baseunit(6.1) !default
$input-dropdown-scrollbar-track-background-color: $color-system-neutral-gray-2 !default
$input-dropdown-scrollbar-thumb-background-color: $color-system-neutral-gray-3 !default

.inputDropdown
  position: relative

  ::v-deep .inputContainer
    input
      text-overflow: ellipsis

.scroll
  position: relative
  display: flex
  width: 100%
  min-height: 0
  max-height: $input-dropdown-scroll-max-height
  color: $font-color-dark

  &.focused
    +focus-outline-keyboard

  &[open]
    .options
      top: calc(100% + 2px)
      max-height: baseunit(30)
      padding: $glass-spacing-05 $glass-spacing-1
      border: 1px solid $input-dropdown-scroll-border
      transition: max-height 0.2s ease-in-out

.options
  position: absolute
  left: 0
  right: 0
  top: 0
  padding: 0
  margin: 0
  z-index: 2
  max-height: 0
  overflow: auto
  background-color: $color-system-neutral-white
  border-radius: $input-dropdown-scroll-border-radius
  border: $input-dropdown-scroll-border
  margin: $input-dropdown-scroll-margins
  box-shadow: 0 2px 4px 0 rgba(#333333, 0.08), 0 2px 8px 0 rgba(#333333, 0.12)

  &.focused
    +focus-outline-keyboard

  &::-webkit-scrollbar-track
    border-radius: $input-dropdown-scroll-border-radius
    background-color: $input-dropdown-scrollbar-track-background-color

  &::-webkit-scrollbar
    width: baseunit(0.8)

  &::-webkit-scrollbar-thumb
    border-radius: $input-dropdown-scroll-border-radius
    background-color: $input-dropdown-scrollbar-thumb-background-color

.spinner-wrapper
  color: $input-dropdown-option-font-color
  background-color: $input-dropdown-option-background-color
  padding: $input-dropdown-option-paddings

.option
  min-height: baseunit(3)
  position: relative
  border-radius: $input-dropdown-scroll-border-radius
  border-bottom: none
  padding: $glass-spacing-1
  cursor: pointer
  list-style-type: none
  display: grid
  align-items: center
  color: $glass-color-system-neutral-grey-07
  +font-size(13.33, 18)
  +mq($allo)
    grid-template-columns: 1fr 1fr
  &:hover, &.focused
    background-color: rgba($color-system-neutral-gray-5, 0.08)
    color: $font-color-dark
  &[selected]
    font-weight: bold

.errorMessage
  display: block
  color: $color-system-validation-error

.logo
  +mq($allo)
    margin-left: auto

  svg
    width: auto

.logosize
  height: $input-dropdown-option-height
</style>
